<template>
  <div class="control-scale">
    <div class="scale-bar" :style="{ width: scaleWidth + 'px' }">
      <ys-text type="metadata1">{{ scaleText }}</ys-text>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { getDistance } from 'geolib'
import { useMapManager } from '@/composables/map-x/use-map-manager'
import { getItem } from '@/plugins/storage'
import { getBestFitUnitOfLength, type UnitType } from '@/lib/math'
import type { WebmapMapViewer } from '@/lib/map-x/webmap-viewer-types'
import { YsText } from '@onxmaps/yellowstone-ui'
import {
  feetToMeters,
  milesToMeters,
  kilometersToMeters,
} from '@/components/shared/x-distance.vue'

// Maximum scale bar width in pixels
const MAX_WIDTH = 150

// Initial scale text
const scaleText = ref<string>('--')
// Actual rendered width (adjusted based on rounded distance values)
const scaleWidth = ref<number>(MAX_WIDTH)

const unitType = computed<UnitType>(() => {
  return getItem('unitType')
})

const mapManager = useMapManager()

// MapViewer references
let mapViewer: WebmapMapViewer

// Event handler cleanup
let cleanupHandler: (() => void) | null = null

// Watch unitType changes and update scale
watch(
  unitType,
  () => {
    updateScale()
  },
  { immediate: false }
)

// Calculate and update scale bar
function updateScale() {
  if (!mapViewer) {
    return
  }

  /**
   * Calculate distance in meters for MAX_WIDTH pixels at map center point
   */
  // Calculate scale using the map's geographic center point as the center point along a horizontal line
  // This ensures consistent measurements regardless of pitch or viewport size
  // Get the center geographic coordinate in LonLat, convert to screen space CtxPoint, then measure left/right
  const center = mapViewer.getCenter()
  const centerCtxPoint = mapViewer.convertLonLatToCtxPoint(center)
  // Calculate left edge to right edge of scale bar using center point x minus/plus half MAX_WIDTH
  const leftX = centerCtxPoint[0] - MAX_WIDTH / 2
  const rightX = centerCtxPoint[0] + MAX_WIDTH / 2
  const y = centerCtxPoint[1]
  const leftPoint = mapViewer.convertCtxPointToLonLat([leftX, y])
  const rightPoint = mapViewer.convertCtxPointToLonLat([rightX, y])

  // Calculate real-world distance in meters using geolib
  const distanceInMeters = getDistance(
    { latitude: leftPoint[1], longitude: leftPoint[0] },
    { latitude: rightPoint[1], longitude: rightPoint[0] }
  )

  /**
   * Use getBestFitUnitOfLength to get displayValue based on unitType and distanceInMeters.
   * getBestFitUnitOfLength is used for line distance measurements elsewhere in the app.
   *
   * Scale bar display behavior by vertical:
   *
   * Hunt:
   * - imperial: yards (yds) below 1000 yds (~914m), then miles (mi)
   * - metric: meters (m) below 500m, then kilometers (km)
   *
   * Backcountry:
   * - imperial: feet (ft) below 1000 yds (~914m), then miles (mi)
   * - metric: meters (m) below 500m, then kilometers (km)
   *
   * Offroad/Fish:
   * - imperial: feet (ft) below 100 yds (~91m), yards (yds) 100-1000 yds, then miles (mi)
   * - metric: meters (m) below 500m, then kilometers (km)
   *
   * @returns Formatted string like "150 ft", "250 yds", "1.2 mi", "500 m", "2.5 km"
   */
  const displayValue = getBestFitUnitOfLength({
    length: distanceInMeters,
    from: 'meters',
    unitType: unitType.value,
    labelType: 'abbreviation',
  })

  /**
   * Calculate adjustedWidth
   */
  // Parse the returned string to extract number and unit
  const parts = displayValue.split(' ')
  if (parts.length !== 2) {
    return
  }
  const [distanceStr, unit] = parts
  const roundedDistance = parseFloat(distanceStr.replace(/,/g, ''))

  // Adjust bar width to match the rounded display value
  // Example: 150px = 457m actual, but we display "1500 ft"
  // Convert 1500ft back to meters = 457.2m
  // Ratio = 457.2m / 457m = 1.0004, adjusted width = 150.06px
  let roundedDistanceInMeters = roundedDistance
  if (unit === 'ft') {
    roundedDistanceInMeters = feetToMeters(roundedDistance)
  } else if (unit === 'mi') {
    roundedDistanceInMeters = milesToMeters(roundedDistance)
  } else if (unit === 'km') {
    roundedDistanceInMeters = kilometersToMeters(roundedDistance)
  }
  // 'm' stays as is
  const ratio = roundedDistanceInMeters / distanceInMeters
  const adjustedWidth = Math.round(MAX_WIDTH * ratio)

  /**
   * Update scaleText and scaleWidth to reflect new values
   */
  scaleText.value = displayValue
  scaleWidth.value = adjustedWidth
}

const initializeControlScale = () => {
  // Initial scale calculation
  updateScale()

  // Update scale after map movement completes
  mapManager.events.on('map-x-camera-moveend', updateScale)

  cleanupHandler = () => {
    mapManager.events.off('map-x-camera-moveend', updateScale)
  }
}

onMounted(() => {
  if (mapManager) {
    // Listen for the map-x-ready event
    mapManager.events.once('map-x-ready', () => {
      mapViewer = mapManager.viewer
      initializeControlScale()
    })
  }
})

onUnmounted(() => {
  if (cleanupHandler) {
    cleanupHandler()
  }
})
</script>
<style scoped>
.control-scale {
  pointer-events: none;
  height: 20px;
  background: var(--ys-color-surface-secondary);
  display: inline-block;
  opacity: 0.84;
  padding: 3px;
  vertical-align: bottom;
}

.scale-bar {
  text-align: center;
  color: var(--ys-color-text-tertiary);
  border-color: var(--ys-color-text-tertiary);
  border-style: solid;
  border-width: 0 1px 1px 1px;
  height: 14px;
  background: none;
  box-sizing: border-box;
  padding: 0 5px;
  white-space: nowrap;
  clear: both;
  pointer-events: auto;
  transform: translate(0);
}
</style>
